import { useCallback } from 'react';
import { css } from '@emotion/react';
import moment from 'moment';

import {
  largeSpacing,
  quarterSpacing,
} from 'styles/global_defaults/scaffolding';
import {
  black,
  gray1,
  gray4,
  gray5,
  info,
  warning,
  white,
} from 'styles/global_defaults/colors';
import {
  headerLineHeight,
  semiBoldFontWeight,
  textMediumFontSize,
  textSmallFontSize,
} from 'styles/global_defaults/fonts';

import { useAvailabilityCalendarContext } from './availability-calendar-context';

const weekdays = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

const getDayNumber = (range: string[], index: number) => {
  const day = range[index];
  if (!day) return '';
  const date = moment(day);
  return date.format('D');
};

type GetDayNumberClassParams = {
  range: string[],
  selectedDate: string,
  index: number,
  endDate: string,
};

const getDayNumberClass = ({
  range,
  selectedDate,
  index,
  endDate,
}: GetDayNumberClassParams) => {
  const day = range[index];
  if (!day) return '';

  const date = moment(day);
  const today = moment();
  const selected = moment(selectedDate);
  const end = endDate ? moment(endDate) : null;

  if (date.isSame(selected, 'day')) return 'selected';
  if (date.isSame(today, 'day')) return 'today';
  if (date.isBefore(today, 'day')) return 'past';
  if (end && date.isAfter(end, 'day')) return 'future';

  return '';
};

const styles = (columns) => css`
  display: grid;
  grid-template-columns: repeat(${columns}, 1fr);
  justify-items: center;
  gap: ${quarterSpacing}px;
  background-color: ${info};
  border-bottom: 1px solid ${gray5};

  .weekday {
    font-weight: ${semiBoldFontWeight};
    font-size: ${textMediumFontSize}px;
    line-height: ${headerLineHeight}px;
    color: ${black};
  }

  .day-number {
    font-weight: ${semiBoldFontWeight};
    font-size: ${textSmallFontSize}px;
    height: ${largeSpacing}px;
    width: ${largeSpacing}px;
    color: ${gray1};
    &.today {
      color: ${warning};
    }
    &.past, &.future {
      color: ${gray4};
    }
    &.selected {
      background-color: ${warning};
      color: ${white};
      border-radius: 50%;
    }
  }
`;

const AvailabilityCalendarWeekdays = () => {
  const {
    selectedDate,
    numberOfDays,
    endDate,
    getRange,
  } = useAvailabilityCalendarContext();
  const displayedWeekdays = weekdays.slice(0, numberOfDays);
  const range = getRange();

  const getClassName = useCallback((index: number) => (
    getDayNumberClass({
      range,
      selectedDate,
      index,
      endDate,
    })
  ), [range, selectedDate, endDate]);

  return (
    <div css={styles(numberOfDays)} className='w-100 pb-1 px-1'>
      {displayedWeekdays.map((day, index) => (
        <div key={getDayNumber(range, index)} className='text-center'>
          <div className='weekday'>{day}</div>
          <div
            className={`day-number d-flex align-items-center justify-content-center ${getClassName(index)}`}
          >
            {getDayNumber(range, index)}
          </div>
        </div>
      ))}
    </div>
  );
};

export default AvailabilityCalendarWeekdays;
